import * as React from "react";
import styled from "styled-components";
import FortnoxInvoiceCreatorClient, {
  FilterValues,
  InvoiceCreatorState,
  InvoiceCreatorStateStatus,
  InvoiceData,
  InvoiceRowType,
} from "../fortnox/invoice_creator_client";
import FilterForm from "./FilterForm";
import { DropdownOption } from "../common/ReactDropdown";
import ActionButtons from "./ActionButtons";
import InvoiceList from "./InvoiceList";
import classNames from "classnames";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactModal from "react-modal";
import DatePicker from "../common/DatePicker";
import { formatNumber } from "./InvoiceTable";
import { JsonResponse } from "../fortnox/client";

export interface Props {
  apiEndpoint: string;
  companyTypes: DropdownOption[];
  companies: DropdownOption[];
}

const Wrapper = styled.div.attrs({ className: "row expanded" })`
  & > .columns {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    & > * {
      margin: 0;
    }

    & > .invoices {
      position: relative;

      &.loading {
        cursor: not-allowed;
        user-select: none;

        & input,
        button,
        select,
        textarea {
          pointer-events: none;
          user-select: none;
        }
      }

      & .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        background-color: rgba(255, 255, 255, 0.8);

        & h1 {
          font-size: 1.5rem;
        }
      }

      & h3 .subheader {
        font-size: 0.8rem;
        font-weight: bold;
        margin-left: 0.5rem;
      }
    }
  }
`;

const modalStyle: ReactModal.Styles = {
  overlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1001, // menu bar is 1000
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  content: {
    position: "relative",
    padding: 0,
    boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
    overflow: "auto",
    inset: 0,
    maxWidth: "calc(100% - 1rem)",
  },
};

const ExportModalWrapper = styled.div`
  padding: 20px;

  & h1 {
    font-size: 1.5rem;
  }

  & .table-container {
    overflow: auto;
    max-height: 500px;
    margin-bottom: 1rem;

    & table {
      & td:nth-child(2) {
        text-align: right;
      }
    }
  }
`;

const InvoiceCreatorPage = ({
  apiEndpoint,
  companyTypes,
  companies,
}: Props) => {
  const apiClient = React.useRef<FortnoxInvoiceCreatorClient>();
  const [filterValues, setFilterValues] = React.useState<FilterValues>(() => ({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    companyType: companyTypes[0].value,
    companyId: undefined,
  }));
  const [invoicesState, setInvoicesState] =
    React.useState<JsonResponse<InvoiceCreatorState> | null>(() => null);
  const [selectedInvoices, setSelectedInvoices] = React.useState<InvoiceData[]>(
    [],
  );
  const [pendingExportInvoiceIds, setPendingExportInvoiceIds] = React.useState<
    number[] | null
  >(null);
  const [exportInvoiceDate, setExportInvoiceDate] =
    React.useState<Date>(getEndOfMonth());

  const pendingExportInvoices = React.useMemo<Map<number, InvoiceData>>(() => {
    const result = new Map<number, InvoiceData>();

    if (invoicesState == null || pendingExportInvoiceIds == null) {
      return result;
    }

    if (invoicesState?.data?.invoices != null) {
      for (let invoice of invoicesState.data.invoices) {
        if (pendingExportInvoiceIds.includes(invoice.id)) {
          result.set(invoice.id, invoice);
        }
      }
    }

    return result;
  }, [pendingExportInvoiceIds, invoicesState]);

  React.useEffect(() => {
    apiClient.current = new FortnoxInvoiceCreatorClient(apiEndpoint);
  }, []);

  const onReceiveState = (state: JsonResponse<InvoiceCreatorState>) => {
    if (state.data != null) {
      state.data.invoices.sort((a, b) => {
        const customerNumberOrder = a.customerNo.localeCompare(b.customerNo);

        if (customerNumberOrder != 0) {
          return customerNumberOrder;
        }

        return a.companyType - b.companyType;
      });
    }
  };

  const onClickShowInvoices = async () => {
    if (apiClient.current == null) {
      return;
    }

    const tempState = createDefaultInvoicesState();
    tempState.data!.status = InvoiceCreatorStateStatus.Loading;
    setInvoicesState(tempState);
    setSelectedInvoices([]);

    const invoicesState = await apiClient.current.getInvoices(filterValues);
    onReceiveState(invoicesState);

    setInvoicesState(invoicesState);

    if (invoicesState.data?.invoices.length ?? 0 > 0) {
      setSelectedInvoices([invoicesState.data!.invoices[0]]);
    } else {
      setSelectedInvoices([]);
    }
  };

  const onConfirmInvoices = (invoiceIds: number[]) => {
    setPendingExportInvoiceIds(invoiceIds);
  };

  const onExportInvoices = async (
    invoiceIds: number[],
  ): Promise<JsonResponse<InvoiceCreatorState>> => {
    if (apiClient.current == null) {
      // this should never happen
      throw new Error("Expected apiClient to be set");
    }

    if (invoicesState == null) {
      throw new Error("Expected invoicesState to be set");
    }

    if (invoicesState.status != 200 || invoicesState.data == null) {
      throw new Error(
        "Expected invoicesState.status to be 200 and invoicesState.data to be set",
      );
    }

    setInvoicesState({
      ...invoicesState,
      data: {
        ...invoicesState.data,
        status: InvoiceCreatorStateStatus.CreatingInvoices,
      },
    });

    const newState = await apiClient.current.exportInvoices(
      invoicesState.data.id,
      invoiceIds,
      exportInvoiceDate,
    );
    onReceiveState(newState);

    setInvoicesState(newState);
    setPendingExportInvoiceIds(null);
    return newState;
  };

  const getLoadingText = (): String => {
    if (invoicesState?.data == null) {
      return "Laddar fakturor...";
    }

    switch (invoicesState.data.status) {
      case InvoiceCreatorStateStatus.Loading:
        return "Laddar fakturor...";
      case InvoiceCreatorStateStatus.CreatingInvoices:
        return "Skickar fakturor...";
    }

    // Fallback to generic text
    return "Laddar...";
  };

  return (
    <Wrapper>
      <div className="columns">
        <h3>Filtrering</h3>
        <FilterForm
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          companyTypes={companyTypes}
          companies={companies}
          client={apiClient}
        />
        <ActionButtons
          onClickShowInvoices={onClickShowInvoices}
          disabled={areActionsForbidden(invoicesState)}
        />
        {invoicesState != null &&
          invoicesState.status == 200 &&
          invoicesState.data != null &&
          invoicesState.data.status != null && (
            <div
              className={classNames(
                "invoices",
                invoicesState.data.status ==
                  InvoiceCreatorStateStatus.Loading && "loading",
              )}
            >
              <h3>
                Fakturor
                <span className="subheader">
                  {invoicesState.data.invoices.length}{" "}
                  {invoicesState.data.invoices.length == 1
                    ? "faktura"
                    : "fakturor"}
                </span>
              </h3>
              <InvoiceList
                invoices={invoicesState.data.invoices}
                status={invoicesState.data.status}
                selectedInvoices={selectedInvoices}
                setSelectedInvoices={setSelectedInvoices}
                confirmExportInvoices={onConfirmInvoices}
              />
              {invoicesState.data.status != InvoiceCreatorStateStatus.Ready && (
                <div className="loading-overlay">
                  <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                  <h1>{getLoadingText()}</h1>
                </div>
              )}
            </div>
          )}
        {invoicesState != null && invoicesState.error != null && (
          <div className="error">
            <h3>Ett fel uppstod</h3>
            <p
              dangerouslySetInnerHTML={parseStringToHtml(
                invoicesState.error?.message,
              )}
            ></p>
          </div>
        )}
      </div>
      <ReactModal
        ariaHideApp={false}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        style={modalStyle}
        isOpen={pendingExportInvoiceIds != null}
        onRequestClose={() => {
          setPendingExportInvoiceIds(null);
        }}
      >
        {pendingExportInvoiceIds != null && (
          <ExportModalWrapper>
            {pendingExportInvoiceIds.length == 1 ? (
              <h1>
                Exportera faktura för{" "}
                {
                  pendingExportInvoices.get(pendingExportInvoiceIds[0])
                    ?.customerName
                }
              </h1>
            ) : (
              <h1>Exportera {pendingExportInvoiceIds.length} fakturor</h1>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                onExportInvoices(pendingExportInvoiceIds);
              }}
            >
              {pendingExportInvoiceIds.length > 1 && (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <td>Kund</td>
                        <td>À-pris (ex moms)</td>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(pendingExportInvoices.values()).map(
                        (invoice) => (
                          <tr key={invoice.customerNo}>
                            <td>
                              <CompanyTypeIcon
                                companyType={invoice.companyType}
                              />
                              {invoice.customerNo} - {invoice.customerName}
                            </td>
                            <td>{formatNumber(invoice.priceSum)}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <label className="invoice-date">
                Fakturadatum
                <DatePicker
                  value={exportInvoiceDate}
                  onChange={(d) => {
                    setExportInvoiceDate(d);
                  }}
                />
              </label>
              <input
                type="submit"
                className="button primary"
                value="Skicka"
                disabled={areActionsForbidden(invoicesState)}
              />
            </form>
          </ExportModalWrapper>
        )}
      </ReactModal>
    </Wrapper>
  );
};

function getLastMonth(): Date {
  var date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getEndOfMonth(): Date {
  let date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() + 1);
  date.setDate(date.getDate() - 1);
  return date;
}

function getDefaultStartDate(): Date {
  return getLastMonth();
}

function getDefaultEndDate(): Date {
  let date = getLastMonth();
  date.setMonth(date.getMonth() + 1);
  date.setDate(1);
  date.setDate(date.getDate() - 1);
  return date;
}

function getTomorrowDate(): Date {
  let date = new Date();
  date.setDate(date.getDate() + 1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function createDefaultInvoicesState(): JsonResponse<InvoiceCreatorState> {
  return {
    error: undefined,
    status: 200,
    data: {
      id: "",
      status: undefined,
      invoices: [],
    },
  };
}

export function areActionsForbidden(
  state: JsonResponse<InvoiceCreatorState> | null | InvoiceCreatorStateStatus,
) {
  if (typeof state === "number") {
    return state != InvoiceCreatorStateStatus.Ready;
  }

  return (
    state?.data != null && state.data.status != InvoiceCreatorStateStatus.Ready
  );
}

export function isDateInCurrentOrFutureMonth(inputDate: Date): boolean {
  const now = new Date();

  return (
    inputDate.getMonth() >= now.getMonth() &&
    inputDate.getFullYear() >= now.getFullYear()
  );
}

function parseStringToHtml(text: string): { __html: string } {
  text = text.replaceAll(/</g, "&lt;");
  text = text.replaceAll(/>/g, "&gt;");
  text = text.replaceAll(/&/g, "&amp;");
  text = text.replaceAll(/\n/g, "<br />");

  return { __html: text };
}

function getCompanyTypeName(type: number) {
  switch (type) {
    case 1:
      return "Städ";
    case 2:
      return "Måleri";
    case 3:
      return "Pensionat";
  }

  return "";
}

export function CompanyTypeIcon({ companyType }: { companyType: number }) {
  return (
    <i
      style={{ marginRight: "0.5rem" }}
      className={classNames(
        "company-type",
        companyType,
        companyType == 1 && "fa-solid fa-spray-can-sparkles",
        companyType == 2 && "fa-solid fa-paint-roller",
        companyType == 3 && "fa-solid fa-house",
      )}
      title={getCompanyTypeName(companyType)}
    ></i>
  );
}

export default InvoiceCreatorPage;
